/**
 * A mission in progress: the encounter, plus the objective states and running
 * tallies that turn a dogfight into a *sortie*.
 *
 * Exactly the same shape as the encounter layer below it — an immutable state
 * and a pure `step` — so a mission can be driven headlessly at full speed by a
 * test, or one tick at a time by the render loop, and produce the same result.
 *
 * The mission ends the moment it is decided. That is not the same as the
 * encounter ending: a Zeppelin hunt is over when the airship burns even though
 * its escort is still up, and a patrol is over when the player crosses back
 * over their own lines with everybody in the sky still healthy.
 */

import {
  createEncounter,
  stepEncounter,
  type CombatAircraft,
  type EncounterState,
} from "../combat/encounter.ts";
import { horizontalDistanceM } from "../combat/route.ts";
import type { AircraftStatus } from "../combat/outcome.ts";
import { targetProfileOf } from "../combat/targets.ts";
import { NO_SCENERY, type SceneryQuery } from "../combat/scenery-fires.ts";
import { parachuteAloft } from "../combat/parachute.ts";
import { FLAT_GROUND, type GroundQuery } from "../flight/ground.ts";
import type { FlightInputs } from "../flight/inputs.ts";
import { FIXED_DT } from "../flight/units.ts";
import {
  evaluateObjective,
  initialObjectiveState,
  isHomecoming,
  type MissionObjective,
  type ObjectiveContext,
  type ObjectiveState,
} from "./objectives.ts";
import { EMPTY_TALLY, foldKills, type KillContext, type KillTally, type VictoryRecord } from "./kill-credit.ts";
import { encounterSetupFrom, flightById, type MissionSpec } from "./mission-spec.ts";
import { isFriendlyTerritory } from "./mission-world.ts";
import { isRaidPlan, raidOutcomeOf, type RaidOutcome } from "./raid.ts";

export const MISSION_OUTCOMES = ["inProgress", "success", "failure"] as const;

export type MissionOutcome = (typeof MISSION_OUTCOMES)[number];

export interface MissionStats {
  readonly ticks: number;
  readonly durationS: number;
  /** Enemy aeroplanes the player personally shot down. */
  readonly aircraftKills: number;
  readonly balloonKills: number;
  readonly zeppelinKills: number;
  /** Every enemy aeroplane the player's side shot down, the player included. */
  readonly squadronKills: number;
  /** The same, for the other two victory categories (`kill-credit.ts`). */
  readonly squadronBalloonKills: number;
  readonly squadronZeppelinKills: number;
  /** Friendly aeroplanes lost, the player's own included. */
  readonly friendlyLosses: number;
  /** Friendly aeroplanes the player shot down himself — a Board of Inquiry offence. */
  readonly friendlyKills: number;
  /** Enemy ground targets his side levelled, and friendly ones lost (`kill-credit.ts`). */
  readonly groundTargetsDestroyed: number;
  readonly friendlyGroundTargetsLost: number;
  /**
   * The subset of those his *own* bombs did — a number that can only be
   * non-zero on a mission that gave him a rack (`mission-options.ts`'s
   * `playerBombs`, a port extension).
   *
   * Deliberately **not** folded into `victories`. `PS.EXE`'s debriefing screen
   * counts AIRCRAFT, BALLOONS and ZEPPELINS and has no column for a shed
   * (docs/game/ui-menus.md), and a career that quietly promoted a railway yard
   * to a confirmed victory would be inventing the original's scoring rather
   * than porting it.
   */
  readonly playerGroundTargetsDestroyed: number;
  /** How the raid went, for the four missions that are one. `undefined` otherwise. */
  readonly raid: RaidOutcome | undefined;
  /** One entry per kill of the player's, with what could be witnessed of it. */
  readonly victories: readonly VictoryRecord[];
  readonly roundsFired: number;
  readonly hitsScored: number;
  readonly hitsTaken: number;
  readonly playerStatus: AircraftStatus;
  readonly playerWounded: boolean;
  /** The pilot himself, not the aeroplane: shot in the cockpit, or bled out. */
  readonly pilotKilled: boolean;
  /** Which side of the lines the player finished the sortie over. */
  readonly overFriendlyTerritory: boolean;
  /**
   * The player has been off the ground at some point in this sortie. Latches.
   * A Scramble starts parked on its own aerodrome, so without this every
   * landing test would be satisfied before the pilot had opened the throttle.
   */
  readonly everAirborne: boolean;
  /** The player is down, in one piece, and stopped, having flown — wherever that happened to be. */
  readonly landedSafely: boolean;
  /**
   * Down safely *at the home aerodrome*, which is what the manual's "bonus
   * points for landing at your own aerodrome upon completion of your mission"
   * is paid on. Always false for a mission with no `homeBase`.
   */
  readonly landedAtBase: boolean;
  /** The undercarriage folded: the aeroplane is written off even if the pilot walked away. */
  readonly gearCollapsed: boolean;
  /**
   * Who shot the player down, by the callsign the mission gave him — `"Werner
   * Voss"` on a Dogfight a Famous Ace, `"Jasta 2 3"` on a squadron scrap. This
   * is `SIMEND.DAT`'s `%a` (`simend-text.ts`, *"%a has shot you down."*), and
   * without it `VANQUISHED!`'s named page is unreachable.
   *
   * `undefined` whenever the port cannot honestly name a person: the player
   * came home, or he went down to flak, to a collision with the ground, or to
   * anything whose id is not a flight in this mission's own roster. The
   * original's other `VANQUISHED!` sequence — the one with no name in it — is
   * what those cases still get.
   *
   * Read off `CombatAircraft.lastAttackerId`, which the combat layer already
   * latches (`encounter-settle.ts`) and already hashes. Nothing is *added* to
   * the encounter here, so the engine fingerprint does not move.
   */
  readonly killedBy: string | undefined;
  /**
   * The pilot took to the silk (AD-021, a declared port enhancement — the
   * original has no bail-out; `docs/engineering/bail-out-design.md`).
   *
   * Set once he jumps, whatever became of him afterwards, so it is *"he left
   * the aeroplane"* and not *"he got away with it"*. Whether he got away with
   * it is `pilotKilled` and `overFriendlyTerritory`, both of which this
   * repoints at the man once there is a canopy (`bailingOf`).
   */
  readonly baledOut: boolean;
}

export interface MissionRun {
  readonly spec: MissionSpec;
  readonly encounter: EncounterState;
  readonly objectives: readonly ObjectiveState[];
  readonly outcome: MissionOutcome;
  readonly stats: MissionStats;
}

function tallyOf(stats: MissionStats): KillTally {
  return {
    aircraftKills: stats.aircraftKills,
    balloonKills: stats.balloonKills,
    zeppelinKills: stats.zeppelinKills,
    squadronKills: stats.squadronKills,
    squadronBalloonKills: stats.squadronBalloonKills,
    squadronZeppelinKills: stats.squadronZeppelinKills,
    friendlyLosses: stats.friendlyLosses,
    friendlyKills: stats.friendlyKills,
    victories: stats.victories,
  };
}

/**
 * Structures off the board, by whose they were. Read off the targets rather than
 * folded from `destroyed` events, because a shed that is alight is already lost
 * and the mission is normally decided several seconds before it finishes
 * burning — an event-driven tally would report zero on exactly the missions it
 * exists for.
 */
function groundTally(spec: MissionSpec, encounter: EncounterState): {
  readonly destroyed: number;
  readonly lost: number;
  readonly byPlayer: number;
} {
  const playerSide = encounter.aircraft.find((entry) => entry.id === spec.playerFlightId)?.side ?? "friendly";
  let destroyed = 0;
  let lost = 0;
  let byPlayer = 0;
  for (const target of encounter.targets) {
    const profile = targetProfileOf(target.kind);
    if (profile.targetClass !== "ground") continue;
    // A shot-out searchlight is not a levelled factory. It is on the roster and
    // it is on the ground, but nothing strategic turns on it (AD-017 §11), and
    // letting it through here would pay a pilot the raid's own 120 points for
    // wrecking a lamp — and dock his squadron 90 for losing one.
    if (!profile.strategic) continue;
    if (!target.destroyed && !target.burning) continue;
    if (target.side === playerSide) {
      lost += 1;
      continue;
    }
    destroyed += 1;
    // `lastAttackerId` is the same last-man-in rule aircraft kill credit runs
    // on (`kill-credit.ts`): whoever put the final bomb or burst into it before
    // it caught. A structure the player set alight and somebody else then
    // cratered would be credited to the second man, which is exactly what
    // happens to an aeroplane and is left consistent on purpose.
    if (target.lastAttackerId === spec.playerFlightId) byPlayer += 1;
  }
  return { destroyed, lost, byPlayer };
}

function killContext(spec: MissionSpec, encounter: EncounterState): KillContext {
  return {
    playerId: spec.playerFlightId,
    frontLine: spec.frontLine,
    encounter,
    callsignOf: (id) => flightById(spec, id)?.callsign ?? id,
  };
}

function objectiveContext(spec: MissionSpec, encounter: EncounterState, armed: boolean): ObjectiveContext {
  return { encounter, playerId: spec.playerFlightId, frontLine: spec.frontLine, armed, raid: spec.raid };
}

/**
 * Objectives are evaluated in two passes because the homecoming ones
 * (`returnToBase`, `landAtBase`) depend on the others: everything else first,
 * then the arming flag, then the way home.
 */
function evaluateAll(
  objectives: readonly MissionObjective[],
  previous: readonly ObjectiveState[],
  make: (armed: boolean) => ObjectiveContext,
): ObjectiveState[] {
  const unarmed = make(false);
  const first = objectives.map((objective, index) =>
    isHomecoming(objective)
      ? (previous[index] ?? initialObjectiveState(objective))
      : evaluateObjective(objective, unarmed, previous[index] ?? initialObjectiveState(objective)),
  );
  const armed = objectives.every(
    (objective, index) => isHomecoming(objective) || objective.role !== "primary" || first[index]?.status === "complete",
  );
  const context = make(armed);
  return objectives.map((objective, index) => {
    const state = first[index] ?? initialObjectiveState(objective);
    if (!isHomecoming(objective)) return state;
    return evaluateObjective(objective, context, state);
  });
}

export function missionOutcomeOf(
  objectives: readonly MissionObjective[],
  states: readonly ObjectiveState[],
): MissionOutcome {
  if (states.some((state) => state.status === "failed")) return "failure";
  const primaries = objectives.filter((objective) => objective.role === "primary");
  if (primaries.length === 0) return "inProgress";
  const done = primaries.every(
    (objective) => states.find((state) => state.id === objective.id)?.status === "complete",
  );
  return done ? "success" : "inProgress";
}

/**
 * The one rule no objective list has to spell out: a pilot who has been shot
 * down has not completed the mission. A duel carries only a "shoot him down"
 * objective, and without this it would sit `inProgress` for ever after the
 * player hit the ground.
 *
 * Success on the same tick still wins — if the last enemy went down as you did,
 * you finished the job, and the debrief will say plainly enough that you did
 * not come back.
 */
function resolveOutcome(
  spec: MissionSpec,
  states: readonly ObjectiveState[],
  encounter: EncounterState,
): MissionOutcome {
  // AD-021: the sortie is not over while the pilot is still in the air. This
  // sits *above* the objective verdict deliberately — `stepMissionRun` freezes
  // the moment the outcome leaves `inProgress`, so letting either the
  // objectives or the destroyed-player rule fire here would stop the sim on the
  // tick of the jump and the descent would never be flown. The canopy comes
  // down in a bounded time (`parachute.ts`), so this cannot hold a mission open
  // for ever the way an un-timed-out wreck could.
  if (parachuteAloft(encounter.parachute)) return "inProgress";
  const outcome = missionOutcomeOf(spec.objectives, states);
  if (outcome !== "inProgress") return outcome;
  const player = encounter.aircraft.find((entry) => entry.id === spec.playerFlightId);
  return player && player.status === "destroyed" ? "failure" : "inProgress";
}

/**
 * A landing is a *stopped, intact* aeroplane on the ground — not merely one
 * that touched it. The `homeBase` test is the same circle the `landAtBase`
 * objective uses, so a mission that carries the objective and one that only
 * pays the bonus agree about where "at your aerodrome" is.
 */
function landingOf(spec: MissionSpec, player: CombatAircraft | undefined, wasAirborne: boolean) {
  if (!player) {
    return { everAirborne: wasAirborne, landedSafely: false, landedAtBase: false, gearCollapsed: false };
  }
  const { gear } = player.sim;
  const everAirborne = wasAirborne || !player.sim.readout.grounded;
  const safe = everAirborne && gear.stopped && !gear.collapsed && !player.sim.crashed;
  const base = spec.homeBase;
  const atBase =
    safe && base !== undefined && horizontalDistanceM(player.sim.position, base.position) <= base.radiusM;
  return { everAirborne, landedSafely: safe, landedAtBase: atBase, gearCollapsed: gear.collapsed };
}

/**
 * The three predicates a bail-out changes, and nothing else (AD-021).
 *
 * Kept together in one helper because they are one idea: **once the pilot has
 * left the aeroplane, the questions "is he dead?" and "whose side is he on?"
 * are about the man and not the machine.** Splitting them across `statsFrom`
 * would have left `overFriendlyTerritory` reading the wreck's final resting
 * place while `pilotKilled` read the canopy — two answers to one question,
 * which is the fault this codebase keeps writing lessons about.
 *
 * With no canopy every line here is byte-for-byte what `statsFrom` always did.
 */
function bailingOf(spec: MissionSpec, encounter: EncounterState, player: CombatAircraft | undefined) {
  const chute = encounter.parachute;
  if (!chute) {
    return {
      baledOut: false,
      pilotKilled: player ? !player.sim.pilot.alive : false,
      overFriendlyTerritory: player ? isFriendlyTerritory(spec.frontLine, player.sim.position.z) : false,
    };
  }
  return {
    baledOut: true,
    // A jumped pilot dies only if the canopy never took his weight. He can also
    // still be shot dead in the seat before he jumps, which is the `!alive`
    // half — `bailOut` refuses a dead man, so the two cannot both be true.
    pilotKilled: chute.status === "dead" || (player ? !player.sim.pilot.alive : false),
    // **Where he came down, not where the wreck did.** This is the drama the
    // feature exists for: the wind is live and altitude-banded, so a jump at
    // 2,000 m near the line can drift him across it, and the debriefing has to
    // report the side of the wire his boots are on.
    overFriendlyTerritory: isFriendlyTerritory(spec.frontLine, chute.position.z),
  };
}

function statsFrom(
  spec: MissionSpec,
  encounter: EncounterState,
  tally: KillTally,
  wasAirborne: boolean,
): MissionStats {
  const player = encounter.aircraft.find((entry) => entry.id === spec.playerFlightId);
  const ground = groundTally(spec, encounter);
  return {
    ...tally,
    ...landingOf(spec, player, wasAirborne),
    groundTargetsDestroyed: ground.destroyed,
    friendlyGroundTargetsLost: ground.lost,
    playerGroundTargetsDestroyed: ground.byPlayer,
    ticks: encounter.tick,
    durationS: encounter.timeS,
    roundsFired: player?.roundsFired ?? 0,
    hitsScored: player?.hitsScored ?? 0,
    hitsTaken: player?.hitsTaken ?? 0,
    playerStatus: player?.status ?? "destroyed",
    playerWounded: player?.sim.pilot.wounded ?? false,
    ...bailingOf(spec, encounter, player),
    raid: isRaidPlan(spec.raid) ? raidOutcomeOf(spec.raid, encounter) : undefined,
    killedBy: killerOf(spec, player),
  };
}

/**
 * The name for `MissionStats.killedBy`, or `undefined` when there is nobody to
 * name. `lastAttackerId` is a latch — it survives a burst the player flew home
 * from — so it only counts once the aeroplane is actually destroyed, and it
 * only becomes a *name* when it resolves to a flight in this mission's roster.
 * Flak batteries, ground targets and the player's own id all fall through to
 * `undefined`, which is the unnamed `VANQUISHED!` the original also has.
 */
function killerOf(spec: MissionSpec, player: CombatAircraft | undefined): string | undefined {
  if (!player || player.status !== "destroyed") return undefined;
  const attacker = player.lastAttackerId;
  if (attacker === null || attacker === spec.playerFlightId) return undefined;
  return flightById(spec, attacker)?.callsign;
}

/**
 * `ground` must be the same `GroundQuery` the run will be stepped with. It is
 * read once, to seat the flights that start on their wheels on the terrain that
 * is actually under them (`mission-spec.ts`'s `seatOnGround`) — a ground start
 * placed on the flat datum over ground that is 22 m lower is an aeroplane
 * dropped onto its undercarriage, which is what the Somme's Scramble was.
 */
export function createMissionRun(spec: MissionSpec, ground: GroundQuery = FLAT_GROUND): MissionRun {
  const encounter = createEncounter(encounterSetupFrom(spec, ground));
  const objectives = evaluateAll(spec.objectives, spec.objectives.map(initialObjectiveState), (armed) =>
    objectiveContext(spec, encounter, armed),
  );
  return {
    spec,
    encounter,
    objectives,
    outcome: resolveOutcome(spec, objectives, encounter),
    stats: statsFrom(spec, encounter, EMPTY_TALLY, false),
  };
}

/**
 * One tick. A decided mission is frozen: stepping it again is a no-op, so a
 * render loop that keeps calling after the debrief appears cannot keep flying
 * the aeroplane behind it.
 */
export function stepMissionRun(
  run: MissionRun,
  inputs: Readonly<Record<string, FlightInputs>> = {},
  dt: number = FIXED_DT,
  ground: GroundQuery = FLAT_GROUND,
  scenery: SceneryQuery = NO_SCENERY,
): MissionRun {
  if (run.outcome !== "inProgress") return run;
  const encounter = stepEncounter(run.encounter, inputs, dt, ground, scenery);
  const objectives = evaluateAll(run.spec.objectives, run.objectives, (armed) =>
    objectiveContext(run.spec, encounter, armed),
  );
  const tally = foldKills(tallyOf(run.stats), killContext(run.spec, encounter));
  return {
    spec: run.spec,
    encounter,
    objectives,
    outcome: resolveOutcome(run.spec, objectives, encounter),
    stats: statsFrom(run.spec, encounter, tally, run.stats.everAirborne),
  };
}

export function objectiveStateOf(run: MissionRun, id: string): ObjectiveState | undefined {
  return run.objectives.find((state) => state.id === id);
}

/** The objective a HUD should be showing: the first primary still outstanding. */
export function activeObjective(run: MissionRun): MissionObjective | undefined {
  return run.spec.objectives.find(
    (objective) =>
      objective.role === "primary" && objectiveStateOf(run, objective.id)?.status === "pending",
  );
}
